import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, Bed, Bath, Ruler, MapPin, Calendar, Car, Home, Mail, AlertTriangle, XCircle } from 'lucide-react';
import { getListingDetail, getListings, trackEvent, trackCreaEvent } from '../lib/api';
import { CreaComplianceFooter } from '../components/CreaCompliance';
import { ListingCard } from '../components/ListingCard';
import SEO from '../components/SEO';

export default function ListingDetail() {
  const { mls } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [similarListings, setSimilarListings] = useState([]);

  useEffect(() => {
    trackEvent({ event_type: 'listing_viewed', metadata: { mls } });
    getListingDetail(mls).then(r => {
      setListing(r.data);
      // Fire CREA Analytics event (mandatory compliance)
      if (r.data?.ddf_id && r.data?.status === 'active') {
        trackCreaEvent({ listing_id: r.data.ddf_id, event_type: 'ListingDetailViewed' });
      }
      // Fetch similar active listings from same neighbourhood
      if (r.data?.neighbourhood_slug) {
        getListings({ neighbourhood: r.data.neighbourhood_slug, limit: 3 })
          .then(sr => setSimilarListings((sr.data.listings || []).filter(l => l.mls_number !== mls)))
          .catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [mls]);

  if (loading) return <div className="min-h-screen pt-16 flex items-center justify-center"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  if (!listing) return <div className="min-h-screen pt-16 flex flex-col items-center justify-center gap-4"><p className="text-slate-500">Listing not found</p><Link to="/homes-for-sale"><Button variant="outline" className="rounded-full">Browse Listings</Button></Link></div>;

  const isRemoved = listing.status === 'removed' || listing.status === 'expired' || listing.status === 'sold';
  const addr = listing.address || {};
  const bld = listing.building || {};
  const land = listing.land || {};
  const photos = listing.photos || [];
  const street = addr.street || `${addr.street_number || ''} ${addr.street_name || ''}`.trim();
  const price = listing.price ? `$${listing.price.toLocaleString()}` : 'Price TBD';
  const hoodSlug = listing.neighbourhood_slug;
  const hoodName = hoodSlug ? hoodSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Burlington';

  return (
    <div className="min-h-screen pt-16" data-testid="listing-detail-page">
      <SEO
        title={isRemoved
          ? `${street}, Burlington — No Longer Available | Previously Listed at ${price}`
          : `${street}, Burlington — ${bld.bedrooms || '?'} Bed ${listing.property_category || ''} | ${price}`}
        description={isRemoved
          ? `${street} in ${hoodName}, Burlington ON is no longer on the market. Browse other homes for sale in ${hoodName}.`
          : `${street} in ${hoodName}, Burlington ON. ${bld.bedrooms || 0} bedrooms, ${bld.bathrooms || 0} bathrooms. ${listing.property_category || 'Property'} listed at ${price}. MLS® ${listing.mls_number}.`}
        canonical={`/listing/${mls}`}
        type="article"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Homes for Sale', url: '/homes-for-sale' },
          { name: street || 'Listing', url: `/listing/${mls}` },
        ]}
        jsonLd={isRemoved ? undefined : {
          "@context": "https://schema.org",
          "@type": "RealEstateListing",
          "name": `${street}, Burlington`,
          "url": `https://buyinburlington.ca/listing/${mls}`,
          "datePosted": listing.list_date || undefined,
          "description": listing.description?.substring(0, 500) || '',
          "image": listing.photos?.[0]?.large || listing.photos?.[0]?.medium || undefined,
          "offers": {
            "@type": "Offer",
            "price": listing.price || undefined,
            "priceCurrency": "CAD",
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": street,
            "addressLocality": "Burlington",
            "addressRegion": "ON",
            "postalCode": addr.postal_code || '',
            "addressCountry": "CA",
          },
          ...(addr.lat && addr.lng ? { "geo": { "@type": "GeoCoordinates", "latitude": addr.lat, "longitude": addr.lng } } : {}),
          "numberOfRooms": bld.bedrooms || undefined,
          "numberOfBathroomsTotal": bld.bathrooms || undefined,
        }}
      />

      {/* Removed/Expired Banner */}
      {isRemoved && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">This property is no longer on the market</p>
                <p className="text-xs text-amber-700 mt-0.5">This listing has been removed, sold, or expired. The information below was accurate as of the last update. Browse current listings below.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
        <Link to="/homes-for-sale" className="inline-flex items-center text-sm text-slate-400 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1" /> All Listings
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main */}
          <div className="lg:col-span-8">
            {/* Photo Gallery */}
            {photos.length > 0 ? (
              <div className="relative rounded-2xl overflow-hidden bg-slate-100 mb-6">
                <img src={photos[photoIndex]?.large || photos[photoIndex]?.medium} alt={street} className={`w-full aspect-[16/10] object-cover ${isRemoved ? 'opacity-80 grayscale-[30%]' : ''}`} />
                {/* Off Market Badge */}
                {isRemoved && (
                  <div className="absolute top-4 left-4 bg-slate-900/90 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm font-semibold">Off Market</span>
                  </div>
                )}
                {photos.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5">
                    <button onClick={() => setPhotoIndex(i => (i - 1 + photos.length) % photos.length)} className="text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /></button>
                    <span className="text-white text-xs font-mono">{photoIndex + 1}/{photos.length}</span>
                    <button onClick={() => setPhotoIndex(i => (i + 1) % photos.length)} className="text-white/70 hover:text-white"><ArrowRight className="w-4 h-4" /></button>
                  </div>
                )}
                {photos.length > 1 && (
                  <div className="flex gap-1 mt-2 overflow-x-auto pb-2">
                    {photos.slice(0, 8).map((p, i) => (
                      <button key={i} onClick={() => setPhotoIndex(i)} className={`w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${i === photoIndex ? 'border-emerald-500' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                        <img src={p.thumbnail || p.medium} alt={`${street} photo ${i + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl bg-slate-100 aspect-[16/10] flex items-center justify-center mb-6 text-slate-300">No photos available</div>
            )}

            {/* Price + Address */}
            <div className="mb-6">
              <h1 className="font-mono text-3xl font-bold text-slate-900 mb-1">{price}</h1>
              <p className="text-lg text-slate-700 font-medium">{street}</p>
              <p className="text-sm text-slate-400">{addr.city || 'Burlington'}, {addr.province || 'ON'} {addr.postal_code || ''}</p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { icon: Bed, label: 'Bedrooms', value: bld.bedrooms || '—' },
                { icon: Bath, label: 'Bathrooms', value: bld.bathrooms || '—' },
                { icon: Ruler, label: 'Size', value: bld.size_interior || '—' },
                { icon: Car, label: 'Parking', value: listing.parking_total || '—' },
              ].map(s => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                  <s.icon className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                  <p className="font-mono text-lg font-bold text-slate-900">{s.value}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Description */}
            {listing.description && (
              <div className="mb-8">
                <h2 className="text-xl tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>About this property</h2>
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{listing.description}</p>
              </div>
            )}

            {/* Property Details */}
            <div className="mb-8">
              <h2 className="text-xl tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>Property details</h2>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  {[
                    ['Type', bld.type || listing.property_category],
                    ['Style', bld.attachment],
                    ['Stories', bld.stories],
                    ['Year Built', null],
                    ['Lot Size', land.size],
                    ['Frontage', land.frontage],
                    ['Basement', bld.basement],
                    ['Heating', bld.heating],
                    ['Exterior', bld.exterior],
                    ['Parking', listing.parking_total],
                    ['Ownership', listing.ownership_type],
                    ['MLS® #', listing.mls_number],
                  ].filter(([_, v]) => v).map(([label, value], i) => (
                    <div key={label} className={`px-4 py-3 ${i % 2 === 0 ? '' : ''} ${Math.floor(i / 2) > 0 ? 'border-t border-slate-100' : ''}`}>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
                      <p className="text-sm text-slate-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Rooms */}
            {listing.rooms?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>Rooms</h2>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {listing.rooms.map((r, i) => (
                    <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                      <span className="text-sm text-slate-700">{r.type}</span>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {r.level && <span>{r.level}</span>}
                        {r.dimension && <span className="font-mono">{r.dimension}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Similar Homes — especially important for off-market listings */}
            {similarListings.length > 0 && (
              <div className={`${isRemoved ? 'mt-8 pt-8 border-t-2 border-emerald-200' : 'mt-8 pt-8 border-t border-slate-200'}`}>
                <h2 className="text-xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  {isRemoved ? `Active homes for sale in ${hoodName}` : 'Similar homes nearby'}
                </h2>
                {isRemoved && (
                  <p className="text-sm text-slate-500 mb-4">This property is no longer available. Here are active listings in the same neighbourhood:</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {similarListings.slice(0, 3).map(l => <ListingCard key={l.mls_number} listing={l} />)}
                </div>
                <div className="text-center mt-4">
                  <Link to={`/homes-for-sale?neighbourhood=${hoodSlug}`} className="text-sm text-emerald-600 hover:text-emerald-700">
                    View all {hoodName} listings <ArrowRight className="w-3.5 h-3.5 inline" />
                  </Link>
                </div>
              </div>
            )}

            {/* CREA Compliance Footer */}
            <CreaComplianceFooter
              moreInfoLink={listing.more_info_link}
              agentName={listing.agent?.name}
              officeName={listing.agent?.office}
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* Contact CTA */}
            <div className="bg-slate-950 rounded-2xl p-6 text-center noise-overlay relative overflow-hidden">
              <div className="relative z-10">
                {isRemoved ? (
                  <>
                    <p className="text-sm font-semibold text-white mb-1">This property is off the market</p>
                    <p className="text-xs text-white/40 mb-4">Browse active listings in {hoodName} or contact Karl for similar options</p>
                    <Link to={`/homes-for-sale?neighbourhood=${hoodSlug}`}>
                      <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 rounded-full h-10 text-sm font-medium gap-1.5 mb-2">
                        <Home className="w-4 h-4" /> View Active Listings
                      </Button>
                    </Link>
                    <a href={`mailto:info@karlwulf.com?subject=Looking for homes similar to ${street}, Burlington`}>
                      <Button variant="outline" className="w-full rounded-full h-10 text-sm font-medium gap-1.5 border-white/20 text-white/70 hover:text-white hover:bg-white/10">
                        <Mail className="w-4 h-4" /> Contact Karl
                      </Button>
                    </a>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-white mb-1">Interested in this property?</p>
                    <p className="text-xs text-white/40 mb-4">Contact Karl Wulf, Burlington Buyer Specialist</p>
                    <a href={`mailto:info@karlwulf.com?subject=Inquiry: ${street} (MLS® ${listing.mls_number})`}>
                      <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 rounded-full h-10 text-sm font-medium gap-1.5">
                        <Mail className="w-4 h-4" /> Contact Karl
                      </Button>
                    </a>
                  </>
                )}
              </div>
            </div>

            {/* Neighbourhood */}
            {hoodSlug && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <p className="text-sm font-semibold text-slate-900">This home is in {hoodName}</p>
                </div>
                <div className="space-y-2">
                  <Link to={`/neighbourhoods/${hoodSlug}`} className="block text-xs text-emerald-600 hover:text-emerald-700">
                    Read the full {hoodName} guide &rarr;
                  </Link>
                  <Link to={`/homes-for-sale?neighbourhood=${hoodSlug}`} className="block text-xs text-emerald-600 hover:text-emerald-700">
                    View all {hoodName} listings &rarr;
                  </Link>
                </div>
              </div>
            )}

            {/* Blueprint CTA */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Not sure about this area?</h3>
              <p className="text-xs text-slate-500 mb-3">Take the Buyer Blueprint to see if {hoodName} matches your priorities.</p>
              <Link to="/blueprint">
                <Button variant="outline" className="w-full rounded-full h-9 text-xs">Start Blueprint</Button>
              </Link>
            </div>

            {/* Agent */}
            {listing.agent?.name && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Listed by</p>
                <p className="text-sm font-medium text-slate-900">{listing.agent.name}</p>
                {listing.agent.office && <p className="text-xs text-slate-500">{listing.agent.office}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
